// Shared utilities & constants for Projects (R&D) components

import { Badge } from "../ui/badge";
import { TrendingUp, TrendingDown } from "lucide-react";

export const STATUS_OPTIONS = [
  { value: "all", label: "Todos los estados" },
  { value: "Activo", label: "Activo" },
  { value: "Nuevo / Activo", label: "Nuevo / Activo" },
  { value: "Finalizado", label: "Finalizado" },
  { value: "Finaliza 2026", label: "Finaliza 2026" },
  { value: "Finaliza 2028", label: "Finaliza 2028" },
];

export const STATUS_SELECT_OPTIONS = [
  "Activo", "Nuevo / Activo", "Finalizado",
  "Finaliza 2026", "Finaliza 2027", "Finaliza 2028", "Finaliza 2029",
  "En pausa",
];

export const EMPTY_CIERRE = {
  resumen_ejecutivo: "", objetivos_alcanzados: "", kpis_resultados: [],
  lecciones_aprendidas: [], patentes: [], modelo_negocio: "",
  proximos_pasos: "", equipo: [], documentacion: "", closure_date: "",
};

export const EMPTY_PROJECT_FORM = {
  project_name: "",
  project_type: "",
  status: "Activo",
  year_start: "",
  year_end: "",
  budget: 0,
  financing: 0,
  expenses: 0,
  subsidy: 0,
  financial_savings: 0,
  tax_deduction: 0,
  notes: "",
  project_manager: "",
};

export const KNOWN_TEAM = [
  "David Redondo", "Blanca Almendro", "Paloma Mingo",
  "Elena Muñoz", "Maria Luisa Fombellida", "Maria Useros",
];

export const formatCurrency = (value) => {
  if (value === null || value === undefined || isNaN(value)) return "€0";
  if (Math.round(value) === 0) value = 0; // evita "-0 €" por redondeo de residuales negativos
  const absValue = Math.abs(value);
  if (absValue >= 1_000_000) return `€${(value / 1_000_000).toFixed(2)}M`;
  if (absValue >= 1_000) return `€${(value / 1_000).toFixed(0)}k`;
  return new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(value);
};

export const formatFullCurrency = (value) => {
  if (value === null || value === undefined || isNaN(value)) return "€0";
  if (Math.round(value) === 0) value = 0; // evita "-0 €" por redondeo de residuales negativos
  return new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(value);
};

export const getStatusBadge = (status) => {
  if (!status) return <Badge variant="outline">Sin estado</Badge>;
  if (status.includes("Finaliza")) return <Badge variant="secondary" className="bg-amber-100 text-amber-700">{status}</Badge>;
  if (status === "Finalizado") return <Badge variant="default" className="bg-green-600">{status}</Badge>;
  if (status.includes("Activo")) return <Badge variant="default">{status}</Badge>;
  return <Badge variant="outline">{status}</Badge>;
};

export const getBalanceIndicator = (balance) => {
  if (balance > 0) return <TrendingUp className="w-4 h-4 text-green-500" />;
  if (balance < 0) return <TrendingDown className="w-4 h-4 text-red-500" />;
  return <span className="w-4 h-4" />;
};

/** True when income doesn't equal subvención + ahorro financiero + deducción fiscal (desglose desalineado) */
export const hasIncomeMismatch = (project) => {
  const expected = (project.subsidy || 0) + (project.financial_savings || 0) + (project.tax_deduction || 0);
  return Math.abs(expected - (project.income || 0)) > 1;
};

export const SEVERITY_STYLES = {
  BLOCKER: "bg-red-100 text-red-800 border-red-300",
  HIGH: "bg-red-50 text-red-700 border-red-200",
  MEDIUM: "bg-amber-50 text-amber-700 border-amber-200",
  LOW: "bg-slate-100 text-slate-600 border-slate-200",
  INFO: "bg-sky-50 text-sky-700 border-sky-200",
};

/**
 * Vista "corregida/derivada" (CORRECTED_DERIVED): suma la diferencia documentada
 * suma-anual (2012-2028) vs total-proyecto a cada campo fuente. Nunca se persiste,
 * es puramente una vista alternativa — la fuente (SOURCE_AS_IS) nunca se sobrescribe.
 */
export const getCorrectedView = (project) => {
  const diff = project.annual_vs_total_diff || {};
  if (Object.keys(diff).length === 0) return { ...project, is_derived_view: false };

  const expenses = (project.expenses || 0) + (diff.expenses || 0);
  const subsidy = (project.subsidy || 0) + (diff.subsidy || 0);
  const financial_savings = (project.financial_savings || 0) + (diff.financial_savings || 0);
  const tax_deduction = (project.tax_deduction || 0) + (diff.tax_deduction || 0);
  const budget = (project.budget || 0) + (diff.budget || 0);
  const financing = (project.financing || 0) + (diff.financing || 0);
  const income = subsidy + financial_savings + tax_deduction;
  const balance = income - expenses;

  return {
    ...project,
    expenses, subsidy, financial_savings, tax_deduction, budget, financing, income, balance,
    is_derived_view: true,
  };
};
